import React, { useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { ShieldAlert, ShieldCheck, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export const RISK_LEVELS = [
  { value: "admin", label: "Admin" },
  { value: "sr_director", label: "Sr. Director" },
  { value: "vp", label: "Vice President" },
];
export const RISK_TONE = { admin: "bg-amber-100 text-amber-800 border-amber-200", sr_director: "bg-orange-100 text-orange-800 border-orange-200", vp: "bg-rose-100 text-rose-800 border-rose-200" };
const label = (v) => RISK_LEVELS.find(l => l.value === v)?.label || v;

export default function FacilityRiskCard({ facility, onChange }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [open, setOpen] = useState(false);
  const [level, setLevel] = useState(facility.risk_level || "admin");
  const [reason, setReason] = useState(facility.risk_reason || "");
  const [busy, setBusy] = useState(false);

  const acknowledge = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/facilities/${facility.facility_id}/risk/acknowledge`, { note: null });
      toast.success("Escalation acknowledged"); onChange?.(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const save = async (atRisk) => {
    if (atRisk && !reason.trim()) { toast.error("Describe why this facility is at risk"); return; }
    setBusy(true);
    try {
      const { data } = await api.put(`/facilities/${facility.facility_id}/risk`, { at_risk: atRisk, risk_level: atRisk ? level : null, risk_reason: atRisk ? reason : null });
      toast.success(atRisk ? `Escalated to ${label(level)}` : "Risk cleared");
      setOpen(false); onChange?.(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <>
      <Card className={`p-5 border shadow-none ${facility.at_risk ? "border-rose-300 bg-rose-50 dark:bg-rose-950/30" : "border-slate-200"}`} data-testid="facility-risk-card">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest font-mono-plex text-slate-500 mb-2 flex items-center gap-1">
              {facility.at_risk ? <ShieldAlert className="w-3 h-3 text-rose-600" /> : <ShieldCheck className="w-3 h-3 text-emerald-600" />} At-risk status
            </div>
            {facility.at_risk ? (
              <>
                <div className="flex items-center gap-2 flex-wrap"><Badge variant="outline" className={RISK_TONE[facility.risk_level]} data-testid="risk-level-badge">Escalated to {label(facility.risk_level)}</Badge></div>
                <div className="text-sm text-slate-800 mt-2">{facility.risk_reason}</div>
                <div className="text-[11px] text-slate-500 font-mono-plex mt-1">{(facility.risk_escalated_at || "").slice(0, 10)} · {facility.risk_escalated_by_name}</div>
                {facility.risk_ack ? (
                  <div className="mt-2 inline-flex items-start gap-1.5 rounded-md bg-emerald-50 border border-emerald-200 px-2 py-1 text-xs text-emerald-800" data-testid="risk-ack-badge">
                    <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                    <span>Acknowledged by <b>{facility.risk_ack.by_name || "leadership"}</b> · {(facility.risk_ack.at || "").slice(0, 10)}{facility.risk_ack.note && <span className="block text-emerald-700/80 italic">“{facility.risk_ack.note}”</span>}</span>
                  </div>
                ) : (
                  <div className="mt-2 flex items-center gap-2 flex-wrap">
                    <span className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5" data-testid="risk-ack-pending">Awaiting leadership acknowledgement</span>
                    {isAdmin && <Button size="sm" variant="outline" className="h-6 text-[11px]" disabled={busy} onClick={acknowledge} data-testid="risk-ack-btn"><CheckCircle2 className="w-3 h-3 mr-1" /> Acknowledge</Button>}
                  </div>
                )}
              </>
            ) : <div className="text-sm text-slate-600">Not flagged. Escalate if census, leadership or referral signals turn negative.</div>}
          </div>
          <Button size="sm" variant={facility.at_risk ? "outline" : "default"} className={facility.at_risk ? "" : "bg-rose-600 hover:bg-rose-700 text-white"} onClick={() => { setLevel(facility.risk_level || "admin"); setReason(facility.risk_reason || ""); setOpen(true); }} data-testid="risk-open-btn">
            {facility.at_risk ? "Update" : "Flag at risk"}
          </Button>
        </div>
      </Card>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="risk-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><ShieldAlert className="w-5 h-5 text-rose-600" /> Escalate at-risk facility</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            <div><Label>Escalate to</Label>
              <Select value={level} onValueChange={setLevel}>
                <SelectTrigger data-testid="risk-level"><SelectValue /></SelectTrigger>
                <SelectContent>{RISK_LEVELS.map(l => <SelectItem key={l.value} value={l.value} data-testid={`risk-level-${l.value}`}>{l.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Reason / signals</Label><Textarea rows={3} data-testid="risk-reason" placeholder="DON turnover, census dropped 15%, competitor onsite…" value={reason} onChange={e => setReason(e.target.value)} /></div>
          </div>
          <DialogFooter className="gap-2">
            {facility.at_risk && <Button variant="outline" disabled={busy} onClick={() => save(false)} data-testid="risk-clear-btn"><ShieldCheck className="w-4 h-4 mr-1" /> Clear risk</Button>}
            <Button disabled={busy} onClick={() => save(true)} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="risk-save-btn">Escalate</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
